import {
  Checkbox,
  CheckboxTypography,
  Icon,
  Link,
  Paper,
  Radio,
  RadioGroup,
  RadioTypography,
  Stack,
  TextField,
  Typography,
  useEmailAddressValidator,
  usePhoneNumberValidator,
  useRadioGroupValidator,
  ValidateProps,
  ValidationResult,
  ValidatorProps,
} from '@ace-clublabs/shared-component-library';
import { useGlobalState } from '../../../state/state';
import { useEffect, useState } from 'react';
import { MSSMembers, MSSMobileAuthStatus } from '@lib/types';
import { PhoneType } from '@ace-clublabs/common-membership-api-types';
import { getLoggedInMember } from '../../../state/helpers';
import { UtagHelper, UtagType } from '../../../utils/UtagHelper';

export const ContactMethodInfo = (props: { setFormHasChanged: () => void }) => {
  const { setFormHasChanged } = props;
  const [membershipDetails] = useGlobalState('memberDetails');
  const [tags] = useGlobalState('tags');

  const urlParams = new URLSearchParams(window.location.search);
  const familyMemberCustomerId = urlParams.get('custId') || ''; //only family members will include custId query param
  const isFamilyMemberContact = familyMemberCustomerId ? true : false;

  const memberInfo = isFamilyMemberContact
    ? getFamilyMemberContact(familyMemberCustomerId, membershipDetails?.members)
    : getLoggedInMember(membershipDetails);

  const memberMobileNumberInfo = memberInfo?.phoneNumbers?.find((phone) => phone.type === PhoneType.MOBILE);
  const memberMobileNumber =
    memberMobileNumberInfo?.areaCode && memberMobileNumberInfo?.prefix && memberMobileNumberInfo?.areaCode
      ? `${memberMobileNumberInfo.areaCode}${memberMobileNumberInfo.prefix}${memberMobileNumberInfo.suffix}`
      : '';

  const memberHomeNumberInfo = memberInfo?.phoneNumbers?.find((phone) => phone.type === PhoneType.HOME);
  const memberHomeNumber =
    memberHomeNumberInfo?.areaCode && memberHomeNumberInfo?.prefix && memberHomeNumberInfo?.areaCode
      ? `${memberHomeNumberInfo.areaCode}${memberHomeNumberInfo.prefix}${memberHomeNumberInfo.suffix}`
      : '';
  const hasMobileAndHomePhoneNumber = memberMobileNumberInfo && memberHomeNumberInfo;
  const [isHomePhoneAMobilePhone, setIsHomePhoneAMobilePhone] = useState(
    memberHomeNumberInfo?.isMobilePhone && memberHomeNumberInfo?.isMobilePhone === true ? true : false
  );

  const hideHomePhone = () => {
    //when only mobile
    if (memberMobileNumber !== '' && memberHomeNumber === '') {
      return true;
      //when mobile=home
    } else if (memberMobileNumber === memberHomeNumber) {
      return true;
    } else {
      return false;
    }
  };

  const zeroValuePhoneNumbers = ['0'.repeat(10), '2'.repeat(10), '3'.repeat(10), '5'.repeat(10)];
  const isInvalidMobileNumber = zeroValuePhoneNumbers.includes(memberMobileNumber);
  const isInvalidHomeNumber = zeroValuePhoneNumbers.includes(memberHomeNumber);

  const [formData, setFormData] = useState({
    emailAddress: memberInfo?.emailAddress || '',
    mobilePhoneNumber: !memberMobileNumber || isInvalidMobileNumber ? '' : memberMobileNumber,
    homePhoneNumber: !memberHomeNumber || isInvalidHomeNumber ? '' : memberHomeNumber,
    agreeMobileTerms: true,
    agreeHomePhoneTerms: true,
  });

  const [consentNotResponded, setConsentNotResponded] = useState({
    mobile: memberMobileNumberInfo?.mobileAuthStatus === MSSMobileAuthStatus.NOT_RESPONDED,
    home: memberHomeNumberInfo?.mobileAuthStatus === MSSMobileAuthStatus.NOT_RESPONDED,
  });

  useEffect(() => {
    // focus home and mobile phone fields to force validation errors to appear on
    // initial page load, then focus back to the top of the page
    // so that the cursor does not rest on the home and mobile input fields
    setTimeout(() => {
      const homePhoneInput = document && document.getElementById('contactHomePhone');
      homePhoneInput?.focus({ preventScroll: true });
      setTimeout(() => {
        const mobilePhoneInput = document && document.getElementById('contactMobilePhone');
        mobilePhoneInput?.focus({ preventScroll: true });
        setTimeout(() => {
          const appHeaderBackButton = document && document.getElementById('app-header-back-button');
          appHeaderBackButton?.focus();
          appHeaderBackButton?.blur();
        });
      });
    });
  }, []);

  useEffect(() => {
    // custom handle when form fields are cleared and blur focus to remove prior display of required validation message
    if (hasMobileAndHomePhoneNumber || !memberMobileNumber) {
      if (formData?.mobilePhoneNumber.length == 10 && formData?.homePhoneNumber.length == 0) {
        const homePhoneInput = document && document.getElementById('contactHomePhone');
        homePhoneInput?.focus();
        homePhoneInput?.blur();
      } else if (formData?.homePhoneNumber.length == 10 && formData?.mobilePhoneNumber.length == 0) {
        const mobilePhoneInput = document && document.getElementById('contactMobilePhone');
        mobilePhoneInput?.focus();
        mobilePhoneInput?.blur();
      }
    }
  }, [formData?.mobilePhoneNumber, formData?.homePhoneNumber]);

  const homePhoneValidator = usePhoneNumberValidator({
    required: formData.mobilePhoneNumber.length !== 10,
    validationFeedback: 'immediate',
    validations: [getValidatorIsInvalidNumber('Home', zeroValuePhoneNumbers)],
  });

  const MobilePhoneValidator = usePhoneNumberValidator({
    required: formData.homePhoneNumber.length !== 10 || hideHomePhone(),
    validationFeedback: 'immediate',
    validations: [getValidatorIsInvalidNumber('Mobile', zeroValuePhoneNumbers)],
  });

  const setConsent = (agreeTerms: boolean, type: PhoneType) => {
    if (type === PhoneType.MOBILE) {
      setFormData({ ...formData, agreeMobileTerms: agreeTerms });
      setConsentNotResponded({ ...consentNotResponded, mobile: false });
    } else if (type === PhoneType.HOME) {
      setFormData({ ...formData, agreeHomePhoneTerms: agreeTerms });
      setConsentNotResponded({ ...consentNotResponded, home: false });
    }
    UtagHelper.sendTag(UtagType.link, tags, 'edit member info', {
      cta: `my account:manage my membership:edit member info-${type.toLowerCase()} mcagr ${agreeTerms ? 'yes' : 'no'}`,
    });
  };

  const getDefaultMobileConsentValue = (type: PhoneType) => {
    const phoneNumberInfo = type === PhoneType.MOBILE ? memberMobileNumberInfo : memberHomeNumberInfo;
    if (phoneNumberInfo?.mobileAuthStatus === MSSMobileAuthStatus.CONSENTED) return `agree`;
    else if (phoneNumberInfo?.mobileAuthStatus === MSSMobileAuthStatus.NOT_CONSENTED) return `disagree`;
    return '';
  };

  const MobileConsent = (type: PhoneType, mobileConsentNotResponded: boolean) => (
    <Paper
      sx={{
        backgroundColor: '#FAF8F4',
        padding: '1rem',
        borderRadius: '0.5rem',
        ...(mobileConsentNotResponded && { border: '1px solid #BE3317' }),
      }}
    >
      {mobileConsentNotResponded && (
        <Stack direction="row" alignItems="center" gap="0.25rem" marginBottom="0.5rem">
          <Icon iconName="triangle-exclamation-regular" sx={{ fontSize: '1.5rem', color: '#BE3317' }} />
          <Typography
            variant="b2Medium"
            sx={{ color: '#BE3317' }}
            className={`mobile-consent-required-message-${type}`}
          >
            Mobile consent is required.
          </Typography>
        </Stack>
      )}

      <Typography variant="b3Regular" className={`mobile-consent-description-${type}`} sx={{ color: '#3D4043' }}>
        I agree to calls and texts, using automated technology, from or on AAA’s behalf regarding membership
        products/services and offers. Consent is not a condition of purchase. Message and data rates may apply. I agree
        to the{' '}
        <Link
          variant="b3Regular"
          sx={{ color: '#0156A3' }}
          className="privacy-policy-link"
          onClick={() => window.open('https://www.ace.aaa.com/legal/privacy.html')}
        >
          Privacy Notice
        </Link>
        ,{' '}
        <Link
          variant="b3Regular"
          sx={{ color: '#0156A3' }}
          onClick={() => window.open('https://www.ace.aaa.com/legal/terms-of-use.html')}
          className="terms-and-condition-link"
        >
          Terms & Conditions
        </Link>
        , and{' '}
        <Link
          variant="b3Regular"
          sx={{ color: '#0156A3' }}
          onClick={() => window.open('https://www.ace.aaa.com/legal/text-messaging-programs-terms-and-conditions.html')}
          className="text-terms-link"
        >
          Text Terms
        </Link>
        .
      </Typography>
      <RadioGroup
        variant="text"
        row
        id={`consent-radio-button-${type}`}
        name={`${type}MobileConsent`}
        value={getDefaultMobileConsentValue(type)}
        validator={useRadioGroupValidator()}
        fullWidth
        sx={{ width: '380px', marginLeft: '-0.3rem' }}
      >
        <Radio
          name={`agree${type}Consent`}
          value={`agree`}
          id={`agree${type}Consent`}
          label={<RadioTypography sx={{ fontSize: '1rem !important', color: '#404040' }}>I agree</RadioTypography>}
          onClick={() => setConsent(true, type)}
          sx={{ maxWidth: '155px !important' }}
        />
        <Radio
          name={`disagree${type}Consent`}
          value={`disagree`}
          id={`disagree${type}Consent`}
          label={
            <RadioTypography sx={{ fontSize: '1rem !important', color: '#404040' }}>I don't agree</RadioTypography>
          }
          onClick={() => setConsent(false, type)}
        />
      </RadioGroup>
    </Paper>
  );

  return (
    <div className="contact-method-container">
      <Stack direction={'column'} gap={'24px'} sx={{ marginTop: '16px' }}>
        <TextField
          fullWidth
          name="emailAddress"
          aria-label="emailAddress"
          value={formData.emailAddress}
          label="Email address"
          id="contactEmail"
          required
          validator={useEmailAddressValidator()}
          onChange={(v) => setFormData({ ...formData, emailAddress: v.target.value })}
          onClearButtonClick={() => {
            setFormData({ ...formData, emailAddress: '' });
            setFormHasChanged();
          }}
          data-qm-block="true"
        />
        <Stack className="mobile-number-section" direction={'column'} gap={'16px'}>
          {hideHomePhone() ? (
            <></>
          ) : (
            <Typography variant="b1Medium" className="one-phone-required">
              At least one phone number is required
            </Typography>
          )}

          <TextField
            name="mobilePhone"
            aria-label="mobilePhone"
            value={formData.mobilePhoneNumber}
            label="Mobile number"
            id="contactMobilePhone"
            required
            onChange={(v) => setFormData({ ...formData, mobilePhoneNumber: v.target.value.replace(/[^0-9]/g, '') })}
            onClearButtonClick={() => {
              setFormData({ ...formData, mobilePhoneNumber: '' });
              setFormHasChanged();
            }}
            validator={MobilePhoneValidator}
            sx={{ width: '269px !important' }}
            data-qm-block="true"
          />
          {/* hides Mobile Consent for Mobile phone when Home number is provided and Mobile number is cleared (only one phone required) */}
          {hasMobileAndHomePhoneNumber && formData.mobilePhoneNumber.length == 0 ? (
            <></>
          ) : (
            MobileConsent(PhoneType.MOBILE, consentNotResponded.mobile)
          )}
        </Stack>
        {!hideHomePhone() && (
          <Stack className="home-number-section" direction={'column'} gap={'16px'}>
            <Stack className="home-number-row" direction={'row'} gap={'16px'} alignItems={'flex-start'}>
              <TextField
                name="homePhone"
                aria-label="homePhone"
                value={formData.homePhoneNumber}
                label="Home number"
                id="contactHomePhone"
                required
                onChange={(v) => setFormData({ ...formData, homePhoneNumber: v.target.value.replace(/[^0-9]/g, '') })}
                onClearButtonClick={() => {
                  setFormData({ ...formData, homePhoneNumber: '' });
                  setIsHomePhoneAMobilePhone(false);
                  setFormHasChanged();
                }}
                validator={homePhoneValidator}
                sx={{ width: '269px !important' }}
                data-qm-block="true"
              />
              <Checkbox
                label={
                  <CheckboxTypography
                    sx={{
                      marginTop: '1.6rem !important',
                      fontSize: '1rem !important',
                    }}
                  >
                    This is a mobile phone
                  </CheckboxTypography>
                }
                name="isHomePhoneDeclaredAsMobile"
                onChange={() => {
                  setIsHomePhoneAMobilePhone(!isHomePhoneAMobilePhone);
                }}
                value="isMobilePhone"
                checked={isHomePhoneAMobilePhone}
                id={'checkIsMobilePhone'}
                variant="text"
                sx={{
                  marginTop: '1.7rem !important',
                }}
              />
            </Stack>
            {isHomePhoneAMobilePhone && MobileConsent(PhoneType.HOME, consentNotResponded.home)}
          </Stack>
        )}
      </Stack>
    </div>
  );
};

function getFamilyMemberContact(customerId: string, members: MSSMembers[] | undefined) {
  return members?.find((m) => m.customerId === customerId);
}

function getValidatorIsInvalidNumber(phoneNumberType: 'Home' | 'Mobile', invalidPhoneNumbers: string[]) {
  return function validateIsInvalidNumber(props: ValidateProps<ValidatorProps, string>): ValidationResult<string> {
    const phoneNumberString = typeof props.value === 'string' ? props.value : '';
    const rawPhoneNumber = phoneNumberString.replace('(', '').replace(')', '').replace('-', '').replace(' ', '');
    const isInvalidNumber = invalidPhoneNumbers.includes(rawPhoneNumber);
    return {
      value: props.value,
      isValid: !isInvalidNumber,
      message: `${phoneNumberType} number is invalid.`,
    };
  };
}
